/**
 * Thin client for the API Worker.
 *
 * Token storage: sessionStorage (not localStorage). Trade-off:
 *   - + Closing the tab / browser drops the token. An XSS payload that
 *     manages to grep storage has a much smaller window.
 *   - - The admin has to log in again whenever they restart the browser.
 *     Acceptable for ops staff who don't keep the tab open 24/7.
 *
 * Long-term: move to HttpOnly cookies (Worker-set, SameSite=Strict). Until
 * then this is the cheap win — one-character change with real upside.
 */

// Admin only ever ships to one production target — the Cloudflare Workers
// `little-shark-api`. Hard-coding the prod URL as the fallback means a build
// without VITE_API_BASE_URL still works in production. For local dev against
// `wrangler dev`, drop VITE_API_BASE_URL=http://127.0.0.1:8787 into
// `admin/.env.local` (gitignored).
const PROD_API_URL = 'https://api.littleshark.app';
const configuredApiUrl: unknown = import.meta.env.VITE_API_BASE_URL;
const API_BASE_URL = typeof configuredApiUrl === 'string' && configuredApiUrl !== ''
  ? configuredApiUrl
  : PROD_API_URL;

const TOKEN_STORAGE_KEY = 'little-shark-admin-token';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  // Sweep up any legacy token left behind in localStorage from before the
  // sessionStorage migration — otherwise it lingers as XSS bait.
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // localStorage unavailable in private modes — best effort.
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PUT';
  body?: unknown;
  /** Override default behaviour of pulling token from storage. */
  skipAuth?: boolean;
}

interface ApiErrorBody {
  ok: false;
  error: string;
  message?: string;
  fields?: Record<string, string>;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {};
  const init: RequestInit = {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
  };

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  if (opts.skipAuth !== true) {
    const token = getToken();
    if (token !== null) headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : 'Network error');
  }

  const contentType = res.headers.get('Content-Type') ?? '';
  const parsed: unknown = contentType.includes('application/json')
    ? await res.json().catch(() => null)
    : null;

  if (!res.ok) {
    if (parsed !== null && typeof parsed === 'object' && (parsed as ApiErrorBody).ok === false) {
      const body = parsed as ApiErrorBody;
      throw new ApiError(res.status, body.error, body.message ?? body.error, body.fields);
    }
    throw new ApiError(res.status, 'http_error', `HTTP ${res.status}`);
  }

  return parsed as T;
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}
